import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { connectWallet as connectExternalWallet, getConnectedAccounts } from '../lib/wallet';

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#080a0f] flex items-center justify-center p-6">
    {/* Ambient glow */}
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[400px] bg-cyan-500/[0.06] rounded-full blur-3xl" />
    </div>
    <div className="max-w-md w-full relative">
      {children}
    </div>
  </div>
);

const Logo = () => (
  <div className="flex items-center justify-center gap-2.5 mb-6">
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" overflow="visible" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 10 C9 4, 16 4, 21 10 C26 16, 33 16, 36 10" stroke="#22d3ee" strokeWidth="2.8" strokeLinecap="round"/>
      <path d="M0 19 C6 12, 15 12, 21 19 C26 26, 32 26, 36 19" stroke="#22d3ee" strokeWidth="2.8" strokeLinecap="round"/>
      <path d="M4 28 C9 22, 16 22, 21 28 C26 34, 33 34, 36 28" stroke="#22d3ee" strokeWidth="2.8" strokeLinecap="round"/>
    </svg>
    <span className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent tracking-tight">FlowSub</span>
  </div>
);

const primaryBtn = "w-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-semibold py-3.5 px-6 rounded-xl transition-all text-base disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2";

function UserAuth({ onAuthenticated, onBackToLanding }) {
  const [authMode, setAuthMode] = useState('wallet'); // choose, wallet

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkExistingAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkExistingAuth() {
    const session = localStorage.getItem('userSession');
    if (session) {
      try {
        const userData = JSON.parse(session);
        if (userData.authMethod === 'wallet') {
          const accounts = await getConnectedAccounts();
          if (accounts.length > 0 && accounts[0].toLowerCase() === userData.address.toLowerCase()) {
            onAuthenticated(userData);
            return;
          }
        } else if (userData.authMethod === 'email') {
          const sessionAge = Date.now() - userData.timestamp;
          if (sessionAge < 24 * 60 * 60 * 1000) {
            onAuthenticated(userData);
            return;
          }
        }
        localStorage.removeItem('userSession');
      } catch (err) {
        localStorage.removeItem('userSession');
      }
    }
  }

  async function handleWalletConnect() {
    setError('');
    setLoading(true);

    try {
      const provider = await connectExternalWallet();
      const accounts = await provider.request({ method: 'eth_accounts' });
      const address = accounts[0];

      const message = `Sign this message to login\n\nTimestamp: ${Date.now()}\nAddress: ${address}`;
      const signature = await provider.request({
        method: 'personal_sign',
        params: [message, address]
      });

      const recoveredAddress = ethers.verifyMessage(message, signature);
      if (recoveredAddress.toLowerCase() !== address.toLowerCase()) {
        setError('Signature verification failed');
        setLoading(false);
        return;
      }

      const userData = {
        authMethod: 'wallet',
        address: address,
        signature: signature,
        timestamp: Date.now()
      };

      localStorage.setItem('userSession', JSON.stringify(userData));
      onAuthenticated(userData);
    } catch (err) {
      if (err.code === 4001) {
        setError('Connection rejected by user');
      } else {
        setError('Failed to connect wallet. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const ErrorBox = ({ msg }) => msg ? (
    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/25 rounded-xl text-red-400 text-sm">
      {msg}
    </div>
  ) : null;

  const BackBtn = ({ onClick }) => (
    <button
      onClick={onClick}
      className="text-sm text-white/40 hover:text-white mb-6 flex items-center gap-1 font-medium transition-colors"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
      Back
    </button>
  );

  const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400 flex-shrink-0">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );

  // Auth mode selection
  if (authMode === 'choose') {
    return (
      <Shell>
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-8">
          <div className="text-center mb-8">
            <Logo />
            <h1 className="text-lg font-semibold text-white mb-1">Welcome Back</h1>
            <p className="text-sm text-white/50">Choose how you'd like to access your account</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => setAuthMode('wallet')}
              className={primaryBtn}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
              Continue with Crypto Wallet
            </button>

            {/* Email login disabled
            <button
              onClick={() => setAuthMode('email')}
              className={ghostBtn}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              Continue with Email
            </button>
            */}
          </div>

          {onBackToLanding && (
            <div className="mt-5 text-center">
              <button
                onClick={onBackToLanding}
                className="text-sm text-white/35 hover:text-white flex items-center gap-1 font-medium mx-auto transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                Back to home
              </button>
            </div>
          )}

          <div className="mt-8">
            <div className="border-t border-white/[0.07] mb-5"></div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white/[0.03] p-3 rounded-xl border border-white/[0.07]">
                <div className="font-medium text-white/80 mb-2 text-xs">Crypto Wallet</div>
                <ul className="space-y-1.5 text-xs">
                  {['Most secure', 'True Web3', 'Hardware wallet support'].map(t => (
                    <li key={t} className="flex items-center gap-1.5 text-white/50"><CheckIcon />{t}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/[0.03] p-3 rounded-xl border border-white/[0.07]">
                <div className="font-medium text-white/80 mb-2 text-xs">Email</div>
                <ul className="space-y-1.5 text-xs">
                  {['No wallet needed', 'Easier for beginners', 'Familiar flow'].map(t => (
                    <li key={t} className="flex items-center gap-1.5 text-white/50"><CheckIcon />{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // Email authentication flow (disabled)
  /* if (authMode === 'email') {
    return (
      <Shell>
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-8">
          <BackBtn onClick={() => { setAuthMode('choose'); setStep('input'); setError(''); }} />

          <div className="mb-6">
            <h1 className="text-lg font-semibold text-white mb-1">
              {step === 'input' ? 'Email Login' : 'Verify Code'}
            </h1>
            <p className="text-sm text-white/50">
              {step === 'input'
                ? 'Enter your email to receive a verification code'
                : `Code sent to ${email}`
              }
            </p>
          </div>

          <ErrorBox msg={error} />

          {step === 'input' ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-[10px] font-medium text-white/35 uppercase tracking-widest mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className={inputClass}
                />
              </div>
              <button type="submit" disabled={loading || !email} className={primaryBtn}>
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>Sending...</>
                ) : 'Send Verification Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleEmailVerify} className="space-y-4">
              <div>
                <label className="block text-[10px] font-medium text-white/35 uppercase tracking-widest mb-1.5">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  required
                  className={inputClass + " text-center text-2xl font-mono tracking-widest"}
                />
              </div>
              <button type="submit" disabled={loading || verificationCode.length !== 6} className={primaryBtn}>
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>Verifying...</>
                ) : 'Verify & Login'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('input'); setVerificationCode(''); setError(''); }}
                className="w-full text-white/40 hover:text-white text-sm transition-colors"
              >
                Use different email
              </button>
            </form>
          )}

          <p className="text-xs text-white/30 text-center mt-4 leading-relaxed">
            We'll send a 6-digit code to your email. Your wallet will be automatically created from your email address.
          </p>
        </div>
      </Shell>
    );
  } */

  // Wallet authentication flow
  if (authMode === 'wallet') {
    return (
      <Shell>
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-8">
          <BackBtn onClick={() => { setAuthMode('choose'); setError(''); }} />

          <div className="mb-6">
            <h1 className="text-lg font-semibold text-white mb-1">Wallet Login</h1>
            <p className="text-sm text-white/50">Connect your crypto wallet to access your account</p>
          </div>

          <ErrorBox msg={error} />

          <button
            onClick={handleWalletConnect}
            disabled={loading}
            className={primaryBtn}
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>Connecting...</>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                Connect Wallet
              </>
            )}
          </button>

          <div className="mt-5 bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
            <div className="font-medium text-white/70 text-xs mb-2.5 uppercase tracking-widest">Benefits</div>
            <ul className="text-sm text-white/50 space-y-2">
              {['Most secure authentication method', 'No email or password needed', 'Works with hardware wallets'].map(t => (
                <li key={t} className="flex items-center gap-2">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400 flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-white/30 text-center mt-4 leading-relaxed">
            Don't have a wallet?{' '}
            <a href="https://metamask.io/download/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">
              Download MetaMask
            </a>
          </p>
        </div>
      </Shell>
    );
  }

  return null;
}

export default UserAuth;
